import { useRef } from 'react'
import { intl } from '../../../../../../i18n'
import { IDoesFilterPassParams } from 'ag-grid-community'
import { CustomFilterProps, useGridFilter } from 'ag-grid-react'
import {
  BulkSheetFilter,
  FilterSelect,
  FilterInput,
  FilterFooter,
} from '../common'

export type TextFilter = {
  operator: TextFilterOperator
  value: string
}

export enum TextFilterOperator {
  CONTAINS = 'CONTAINS',
  NOT_CONTAINS = 'NOT_CONTAINS',
  EQUALS = 'EQUALS',
  NOT_EQUALS = 'NOT_EQUALS',
  STARTS_WITH = 'STARTS_WITH',
  ENDS_WITH = 'ENDS_WITH',
  BLANK = 'BLANK',
  NOT_BLANK = 'NOT_BLANK',
}

export const TextFilterOperatorLabel = {
  [TextFilterOperator.CONTAINS]: intl.formatMessage({
    id: 'bulksheet.filter.textFilter.operator.contain',
  }),
  [TextFilterOperator.NOT_CONTAINS]: intl.formatMessage({
    id: 'bulksheet.filter.textFilter.operator.notContain',
  }),
  [TextFilterOperator.EQUALS]: intl.formatMessage({
    id: 'bulksheet.filter.textFilter.operator.equals',
  }),
  [TextFilterOperator.NOT_EQUALS]: intl.formatMessage({
    id: 'bulksheet.filter.textFilter.operator.notEquals',
  }),
  [TextFilterOperator.STARTS_WITH]: intl.formatMessage({
    id: 'bulksheet.filter.textFilter.operator.startsWith',
  }),
  [TextFilterOperator.ENDS_WITH]: intl.formatMessage({
    id: 'bulksheet.filter.textFilter.operator.endsWith',
  }),
  [TextFilterOperator.BLANK]: intl.formatMessage({
    id: 'bulksheet.filter.textFilter.operator.blank',
  }),
  [TextFilterOperator.NOT_BLANK]: intl.formatMessage({
    id: 'bulksheet.filter.textFilter.operator.notBlank',
  }),
}

const BLANK_OPERATORS = [TextFilterOperator.BLANK, TextFilterOperator.NOT_BLANK]

const buildTextFilterModel = (
  operator: TextFilterOperator,
  value: string
): TextFilter | null => {
  const isBlankOp = BLANK_OPERATORS.includes(operator)
  const isActive = isBlankOp || !!value
  return isActive ? { operator, value: isBlankOp ? '' : value } : null
}

type Props = CustomFilterProps

export const ClientSideTextFilter = ({
  model,
  onModelChange,
  getValue,
}: Props) => {
  const inputRef = useRef<HTMLInputElement>(null)

  // Derive state from model (single source of truth)
  const operator =
    (model?.operator as TextFilterOperator) ?? TextFilterOperator.CONTAINS
  const value = (model?.value as string) ?? ''

  useGridFilter({
    doesFilterPass(params: IDoesFilterPassParams) {
      // Don't filter new rows
      if (params.data.isAdded) return true
      const textValue = getValue(params.node)
      if (!textValue && value) return false
      switch (operator) {
        case TextFilterOperator.CONTAINS:
          return textValue.indexOf(value) >= 0
        case TextFilterOperator.NOT_CONTAINS:
          return textValue.indexOf(value) < 0
        case TextFilterOperator.EQUALS:
          return textValue === value
        case TextFilterOperator.NOT_EQUALS:
          return textValue !== value
        case TextFilterOperator.STARTS_WITH:
          return textValue.indexOf(value) === 0
        case TextFilterOperator.ENDS_WITH: {
          const index = textValue.lastIndexOf(value)
          return index >= 0 && index === textValue.length - value.length
        }
        case TextFilterOperator.BLANK:
          return !textValue
        case TextFilterOperator.NOT_BLANK:
          return !!textValue
        default:
          // should never happen
          console.warn('invalid filter type ' + operator)
          return false
      }
    },

    afterGuiAttached() {
      inputRef.current?.focus()
    },

    getModelAsString() {
      if (!model) return ''
      return TextFilterOperatorLabel[operator] + (value ? ` : ${value}` : '')
    },
  })

  return (
    <BulkSheetFilter>
      <FilterSelect
        variant="outlined"
        value={operator}
        onChange={e =>
          onModelChange(
            buildTextFilterModel(e.target.value as TextFilterOperator, value)
          )
        }
        sx={{ width: '100%' }}
      >
        {Object.values(TextFilterOperator).map(v => (
          <option key={`text-filter-option-${v}`} value={v}>
            {TextFilterOperatorLabel[v]}
          </option>
        ))}
      </FilterSelect>
      <FilterInput
        inputRef={inputRef}
        value={value}
        onChange={e =>
          onModelChange(buildTextFilterModel(operator, e.target.value))
        }
        placeholder={'Filter...'}
        sx={{ height: 22 }}
      />
      <FilterFooter loading={false} onClick={() => onModelChange(null)} />
    </BulkSheetFilter>
  )
}
